import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';
import { PropertyState } from 'src/app/workspace/state/property.state';
import { environment } from 'src/environments/environment';
import { CmaService } from '../cma.service';
import { CmaState } from '../cma.state';

@Component({
  selector: 'app-value-adjustments',
  templateUrl: './value-adjustments.component.html',
  styleUrls: ['./value-adjustments.component.scss'],
})

export class ValueAdjustmentsComponent implements OnInit {

  constructor(
    public cmaState: CmaState,
    public propertyState: PropertyState,
    private cmaService: CmaService,
    private dialog: MatDialog,
    private snackbar: MatSnackBar,
    private cdr: ChangeDetectorRef,
  ) { }

  @Input() isEdit: boolean;
  @Input() reportId: string;
  @Input() cmaReportId: string;
  @Input() cmaReportData: any;
  @Output() valuesAdjusted = new EventEmitter<boolean>();
  @ViewChild('weightDialog', { static: false }) weightDialog: any;
  @ViewChild('saveAdjustedValuesDialog', { static: false }) saveAdjustedValuesDialog: any;

  onDestroyNotifier$ = new Subject();
  properties = [];
  oldWeights = [];
  listingImageBaseUrlAlt = environment.listingImageBaseUrlAlt;
  dialogRef: any;
  totalWeight: number = 100;
  userEdited: boolean = false;

  attributes = [
    { key: 'BedroomsTotal', label: 'Bedrooms', count: true },
    { key: 'BathroomsFull', label: 'Bathrooms Full', count: true },
    { key: 'BathroomsHalf', label: 'Bathrooms Half', count: true },
    { key: 'TotalRooms', label: 'Total Rooms', count: true },
    { key: 'LivingArea', label: 'Living Area (sq. ft)', count: true },
    { key: 'LotSizeSquareFeet', label: 'Lot Size (sq. ft)', count: true },
    { key: 'ParkingSpaces', label: 'Covered Parking Spaces,', count: true },
    { key: 'DrivewayParkingSpaces', label: 'Driveway Parking Spaces', count: true },
    { key: 'Age', label: 'Age', count: false },
    { key: 'Stories', label: 'Stories', count: true },
    { key: 'Fireplaces', label: 'Fireplaces', count: true },
    { key: 'BasementTotalArea', label: 'Basement Total Area', count: true },
    { key: 'BasementFinishedArea', label: 'Basement Finished Area', count: true },
    { key: 'BasementRecRooms', label: 'Basement Rec Rooms', count: true },
    { key: 'BasementBedrooms', label: 'Basement Bedrooms', count: true },
    { key: 'BasementBathroomsFull', label: 'Basement Bathrooms Full', count: true },
    { key: 'BasementBathroomsHalf', label: 'Basement Bathrooms Half', count: true },
    { key: 'BasementOtherFacilities', label: 'Basement Other Facilities', count: false },
    { key: 'BasementExitType', label: 'Basement Exit Type', count: false },
    { key: 'PorchPatioDeck', label: 'Porch/Patio/Deck', count: false },
    { key: 'Pool', label: 'Pool', count: false },
    { key: 'ArchitecturalStyle', label: 'Architectural Style', count: false },
    { key: 'Appliances', label: 'Appliances', count: false },
    { key: 'Heating', label: 'Heating', count: false },
    { key: 'Cooling', label: 'Cooling', count: false },
    { key: 'LocationFactor', label: 'Location Factor', count: false },
    { key: 'ViewFactor', label: 'View Factor', count: false },
    { key: 'AttachmentType', label: 'Attachment Type', count: false },
    { key: 'EnergyEfficientItems', label: 'Energy Efficient Items', count: false },
    { key: 'QualityOfConstruction', label: 'Quality of Construction', count: false },
    { key: 'Condition', label: 'Condition', count: false },
    { key: 'CloseDate', label: 'Date of Sale', count: false },
    { key: 'StandardStatus', label: 'Standard Status', count: false },
    { key: 'SaleType', label: 'Sale Type', count: false },
  ];

  allowedKeysForInput = [
    'BedroomsTotal',
    'BathroomsDecimal',
    'BathroomsFull',
    'BathroomsHalf',
    'TotalRooms',
    'LivingArea',
    'LotSizeSquareFeet',
    'ParkingSpaces',
    'DrivewayParkingSpaces',
    'Age',
    'Stories',
    'Fireplaces',
    'BasementTotalArea',
    'BasementFinishedArea',
    'BasementRecRooms',
    'BasementBedrooms',
    'BasementBathroomsFull',
    'BasementBathroomsHalf',
    'BasementOtherFacilities',
  ];


  ngOnInit(): void {
    this.cmaState.valueAdjustment
      .pipe(
        filter(value => value != null), // ignore null/undefined
        take(1) // subscribe only once
      )
      .subscribe((value: any) => {
        this.properties = value;
        const subject = this.cmaReportData?.subject;
        if (!this.properties.includes(subject)) {
          this.properties.unshift(subject);
        }
        this.calculatePercentages();
        this.properties.forEach(p => this.splitBathrooms(p));

        if (this.isEdit) {
          if (this.cmaReportData?.unitAdjustments) {
            this.cmaReportData.unitAdjustments.forEach(ele => {
              this.attributes.forEach(attr => {
                if (attr.key === ele.fieldId) {
                  attr[attr.key + 'Price'] = ele.adjustmentAmount;
                }
              });
            });
          }
          let percentageSum = 0;

          for (let i = 1; i < this.properties.length; i++) {
            const element = this.properties[i];
            if (element?.AdjustmentInfo?.weight !== undefined && element?.AdjustmentInfo?.weight !== null) {
              element.percentage = element.AdjustmentInfo.weight;
              percentageSum += parseFloat(element.percentage);
            }

            if (element?.AdjustmentInfo?.adjustments) {
              element.adjustmentSum = 0;
              element.AdjustmentInfo.adjustments.forEach(adj => {
                element[adj.fieldId + 'Price'] = adj.adjustmentAmount;
                element.adjustmentSum += adj.adjustmentAmount;
              });
            }
          }

          const epsilon = 0.01; // allow 0.01% difference
          if (Math.abs(percentageSum - 100) < epsilon) {
            // Treat it as 100
            for (let i = 1; i < this.properties.length; i++) {
              const element = this.properties[i];
              if (element?.AdjustmentInfo?.weight !== undefined && element?.AdjustmentInfo?.weight !== null) {
                element.percentage = element.AdjustmentInfo.weight;
              }
            }
          } else {
            const editableProps = this.properties.slice(1);
            const count = editableProps.length;

            if (count === 0) return;

            const equalWeight = 100 / count;

            editableProps.forEach((prop, idx) => {
              prop.percentage = Number(equalWeight.toFixed(2));
            });

            // fix rounding difference
            const total = editableProps.reduce((s, p) => s + p.percentage, 0);
            const diff = 100 - total;
            editableProps[editableProps.length - 1].percentage += diff;

            this.totalWeight = 100;
            this.calculateAFMV();
          }

          this.properties = this.properties
            .filter((item) => !!item.PMXPropertyId)
            .sort((a, b) => b.SimilarityScore - a.SimilarityScore)
            .filter((item, index, self) => index === self.findIndex((t) => t.PMXPropertyId === item.PMXPropertyId));

          this.properties[0].adjustedPrice =
            this.cmaReportData?.homeValue?.adjustedValue;
        } else {
          this.calculateAFMV();
        }
        this.cdr.detectChanges();
      });
  }


  isNumber(value: any, key: string): boolean {
    if (this.allowedKeysForInput.includes(key)) {
      value = Number(value) || 0;
    }
    return typeof value === 'number' && !isNaN(value);
  }

  getAbs(value: number): number {
    return Math.abs(value);
  }

  applyAdjustment(a?: any) {
    this.userEdited = true;
    let subjectProperty = this.properties[0];
    for (let index = 1; index < this.properties.length; index++) {
      let property = this.properties[index];
      let listOrClosePrice = Number(property.listOrClosePrice) || 0;
      let adjustmentSum = 0;
      // this.attributes.forEach((a) => {
      const isChecked = property[a.key + 'Checked'];
      const topValue = Number(a[a.key + 'Price']) || 0;

      if (!isChecked) {
        if (this.allowedKeysForInput.includes(a.key)) {
          if (subjectProperty[a.key] && property[a.key]) {
            property[a.key + 'Price'] = (Number(subjectProperty[a.key]) * topValue) - (Number(property[a.key]) * topValue);
          } else if (subjectProperty[a.key]) {
            property[a.key + 'Price'] = Number(subjectProperty[a.key]) * topValue;
          } else if (property[a.key]) {
            property[a.key + 'Price'] = 0 - (Number(property[a.key]) * topValue);
          } else {
            property[a.key + 'Price'] = 0;
          }
        } else if ((!property[a.key + 'Price'] || property[a.key + 'Price'] == '')) {
          property[a.key + 'Price'] = 0;
        }
      } else if (isChecked && (!property[a.key + 'Price'] || property[a.key + 'Price'] == '')) {
        property[a.key + 'Price'] = 0;
      }
      this.attributes.forEach((attr) => {
        if (property[attr.key + 'Price']) {
          adjustmentSum += Number(property[attr.key + 'Price']);
        }
      });
      property.adjustmentSum = Number(adjustmentSum);
      property.adjustedPrice = Number(listOrClosePrice) + Number(adjustmentSum);
    };
    this.calculateAFMV();
  }

  weightageChange(index: number) {
    // Skip base property (index 0)
    this.userEdited = true;
    const editableProps = this.properties.slice(1);
    const weights = editableProps.map(p => p.percentage);

    const editedIndex = index - 1;
    const newWeight = this.properties[index].percentage;

    const updatedWeights = this.adjustWeights(weights, editedIndex, newWeight);

    if (!updatedWeights) {
      // invalid case → revert to previous weight
      // this.properties[index].percentage = weights[editedIndex];
      return;
    }

    // apply updated weights
    updatedWeights.forEach((w, idx) => {
      this.properties[idx + 1].percentage = w;
    });

    this.calculateAFMV();

  }

  adjustWeights(weights: number[], editedIndex: number, newWeight: number): number[] | null {
    const total = 100;
    const updated = [...weights.map(w => Number(w) || 0)]; // ✅ ensure all numeric

    // 1️⃣ Calculate sum of weights above the edited one
    let sumAbove = 0;
    for (let i = 0; i < editedIndex; i++) {
      sumAbove += updated[i];
    }

    // 2️⃣ Validate new weight
    if (sumAbove + newWeight > total || (weights.length == (editedIndex + 1) && sumAbove + newWeight < total)) {
      this.totalWeight = sumAbove + newWeight;
      this.openSnackbar(
        `Total weightage must be exactly 100%. Current total: ${this.totalWeight.toFixed(2)}%`,
        false
      );
      return null;
    }

    // 3️⃣ Update edited weight
    updated[editedIndex] = newWeight;

    // 4️⃣ Recalculate remaining weights
    const remaining = total - (sumAbove + newWeight);
    const belowCount = updated.length - (editedIndex + 1);

    if (belowCount > 0) {
      const equalShare = remaining / belowCount;
      for (let i = editedIndex + 1; i < updated.length; i++) {
        updated[i] = equalShare;
      }
    }

    // 5️⃣ Round and normalize total to exactly 100
    let rounded = updated.map(w => Number(Number(w).toFixed(2))); // ✅ safe rounding
    let diff = total - rounded.reduce((a, b) => a + b, 0);
    if (Math.abs(diff) > 0.001) {
      rounded[rounded.length - 1] = Number((rounded[rounded.length - 1] + diff).toFixed(2));
    }

    this.totalWeight = rounded.reduce((sum, w) => sum + Number(w || 0), 0);
    return rounded;
  }


  calculateAFMV() {
    let adjustedFMV = 0;
    for (let index = 1; index < this.properties.length; index++) {
      adjustedFMV = adjustedFMV + (Number(this.properties[index]?.adjustedPrice) * Number(this.properties[index]?.percentage)) / 100;
    }
    this.properties[0].adjustedPrice = Number(adjustedFMV).toFixed(0);

    this.properties = this.properties
      .filter((item) => !!item.PMXPropertyId)
      .sort((a, b) => b.SimilarityScore - a.SimilarityScore)
      .filter((item, index, self) => index === self.findIndex((t) => t.PMXPropertyId === item.PMXPropertyId));
  }

  calculatePercentages() {
    if (!this.properties || this.properties.length <= 1) return;
    const comps = this.properties.slice(1);
    const percent = 100 / comps.length;
    comps.forEach((p) => {
      p.listOrClosePrice = p.StandardStatus === 'Sold' ? Number(p.ClosePrice) || 0 : Number(p.ListPrice) || 0;
      p.adjustedPrice = Number(p.listOrClosePrice);
      p.percentage = percent.toFixed(2);
    });
  }

  splitBathrooms(property: any) {
    const value = parseFloat(property.BathroomsDecimal) || 0;
    property.BathroomsFull = Math.floor(value);
    property.BathroomsHalf = Math.round((value - Math.floor(value)) * 10);
  }

  openPropertyDetails(property: any) {
    this.propertyState.propertyDetailsSheetInput = property;
  }

  saveAdjustments() {
    let unitAdjustments = [];
    let compAdjustments = [];
    for (let index = 0; index < this.attributes.length; index++) {
      const element = this.attributes[index];
      if (element[element.key + 'Price']) {
        unitAdjustments.push({
          "fieldId": element.key,
          "adjustmentAmount": element[element.key + 'Price']
        })
      }
    }
    for (let index = 1; index < this.properties.length; index++) {
      const element = this.properties[index];
      let adjustments = [];
      for (let i = 0; i < this.attributes.length; i++) {
        const ele = this.attributes[i];
        if (element[ele.key + 'Price']) {
          adjustments.push({
            "fieldId": ele.key,
            "adjustmentAmount": element[ele.key + 'Price']
          });
        }
      }
      compAdjustments.push({
        "listingId": element?.ListingId,
        "pmxPropertyId": element?.PMXPropertyId,
        "adjustmentInfo": {
          "adjustedPrice": element?.adjustedPrice,
          "weight": element?.percentage,
          "adjustments": adjustments
        }
      })
    }

    let params = {
      "referenceId": this.cmaReportId,
      "subjectAdjustedValue": this.properties[0]?.adjustedPrice,
      "unitAdjustments": unitAdjustments,
      "compAdjustments": compAdjustments
    }

    this.cmaService
      .saveAdjustments(params)
      .pipe(takeUntil(this.onDestroyNotifier$))
      .subscribe(({ isLoading, data }) => {

        if (data) {
          let params = {
            "referenceId": this.cmaReportId,
            "selectedValueType": "SALES_COMPARISON_VALUE",
            "selectedValue": this.properties[0]?.adjustedPrice
          }

          this.cmaService
            .saveAdjustedValueSelection(params)
            .pipe(takeUntil(this.onDestroyNotifier$))
            .subscribe(({ isLoading, data }) => {
              if (data) {
                this.openSnackbar('Value adjustments saved successfully!', true);
                this.valuesAdjusted.emit(true);
                this.cmaState.openValueAdjustmentsValue = false;
              }
            });
        }
      });
  }

  openWeightDialog() {
    this.dialogRef = this.dialog.open(this.weightDialog);
    const editableProps = this.properties.slice(1);
    this.oldWeights = editableProps.map(p => p.percentage);
  }

  closeDialog() {
    this.dialogRef.close();
    // apply old weights
    this.oldWeights.forEach((w, idx) => {
      this.properties[idx + 1].percentage = w;
    });
  }

  closeBackDialog() {
    this.dialogRef.close();
  }

  skipSaving() {
    this.dialogRef.close();
    this.cmaState.openValueAdjustmentsValue = false;
  }

  saveAdjustedValuesAndProceed() {
    this.dialogRef.close();
    this.saveAdjustments();
  }

  saveAndProceed() {
    const editableProps = this.properties.slice(1);
    const weights = editableProps.map(p => p.percentage);

    this.totalWeight = weights.reduce((sum, w) => sum + Number(w || 0), 0);

    if (Math.abs(this.totalWeight - 100) > 0.01) {
      this.openSnackbar(
        `Total weightage must be exactly 100%. Current total: ${this.totalWeight.toFixed(2)}%`,
        false);
      return;
    }

    this.dialogRef.close();
  }

  back() {
    if (this.userEdited) {
      this.dialogRef = this.dialog.open(this.saveAdjustedValuesDialog);
    } else {
      this.cmaState.openValueAdjustmentsValue = false;
    }
  }

  openSnackbar(upgradeMsg, success = false) {
    this.snackbar.open(upgradeMsg, 'Dismiss', {
      duration: 5000,
      panelClass: [success ? 'snackbar-success' : 'snackbar-error'],
      verticalPosition: 'top',
    });
  }

  onWheel(event: WheelEvent) {
    (event.target as HTMLElement).blur(); // remove focus to stop scroll changing value
    event.preventDefault(); // block scroll value change
  }

}
